import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { PaymentMethodAddNewItemOption } from 'components/payment-method-add-new-item-option';
import PaymentMethodOption from 'components/payment-method-option';
import { useFlag } from 'utils/launchdarkly';
import noop from 'utils/noop';
import { ADD_NEW_CARD_BUTTON } from 'utils/test-ids';

import { StyledAddNewCreditCardIcon, StyledAddNewGiftCardIcon } from '../styled';

import { PaymentMethodOptionItemDivider, StyledOption } from './styled';
import { PaymentMethodOptionProps } from './types';

function PaymentMethodOptions({
  collapsed,
  setCollapsed,
  unSelectedMethods,
  handleSelect,
  setIsAddNewCardOptionSelected,
  setIsAddNewGiftCardOptionSelected,
  showAddNewGiftCardOption,
  showAddNewCardOption,
  openGiftCardActionSheet = noop,
}: PaymentMethodOptionProps) {
  const { formatMessage } = useIntl();
  const enableGiftCard = useFlag('enable-gift-card');

  const handleUnselectedMethodPress = useCallback(
    (newfdAccountId: any) => {
      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      handleSelect(newfdAccountId);
      setCollapsed(true);
    },
    [handleSelect, setCollapsed, setIsAddNewGiftCardOptionSelected, setIsAddNewCardOptionSelected]
  );

  const handleAddNewCardOptionPress = useCallback(() => {
    setIsAddNewCardOptionSelected(true);
    setIsAddNewGiftCardOptionSelected(false);
    handleSelect();
    setCollapsed(true);
  }, [
    handleSelect,
    setCollapsed,
    setIsAddNewGiftCardOptionSelected,
    setIsAddNewCardOptionSelected,
  ]);

  const handleAddNewGiftCardOptionPress = useCallback(() => {
    setIsAddNewGiftCardOptionSelected(true);
    setIsAddNewCardOptionSelected(false);
    handleSelect();
    setCollapsed(true);
    openGiftCardActionSheet();
  }, [
    setIsAddNewGiftCardOptionSelected,
    setIsAddNewCardOptionSelected,
    handleSelect,
    setCollapsed,
    openGiftCardActionSheet,
  ]);

  if (collapsed) {
    return null;
  }

  return (
    <>
      {unSelectedMethods.map(method => (
        <StyledOption key={method.accountIdentifier}>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodOption
            method={method}
            onPress={handleUnselectedMethodPress}
            disableMethod={!!method.prepaid && !enableGiftCard}
            hideDeleteIcon
            hideBorder
            showGiftCardBalance
          />
        </StyledOption>
      ))}

      {showAddNewCardOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            testID={ADD_NEW_CARD_BUTTON}
            text={formatMessage({ id: 'addNewCard' })}
            Icon={<StyledAddNewCreditCardIcon />}
            onPress={handleAddNewCardOptionPress}
          />
        </StyledOption>
      )}

      {showAddNewGiftCardOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            testID="add-gift-card"
            text="Add New Gift Card"
            Icon={<StyledAddNewGiftCardIcon />}
            onPress={handleAddNewGiftCardOptionPress}
          />
        </StyledOption>
      )}
    </>
  );
}

export default PaymentMethodOptions;
